import type { SourceComment, SourceCommentPage } from '@/sources/types';

export interface CommentPaginationState {
  pages: SourceCommentPage[];
  loading: boolean;
  error: unknown | null;
  inFlightPage: number | null;
}

export type CommentPaginationAction =
  | { type: 'request'; page: number }
  | { type: 'success'; page: SourceCommentPage }
  | { type: 'failure'; error: unknown; page: number }
  | { type: 'retry' }
  | { type: 'reset' };

export function initialCommentPaginationState(): CommentPaginationState {
  return {
    pages: [],
    loading: false,
    error: null,
    inFlightPage: null,
  };
}

export function commentPaginationReducer(
  state: CommentPaginationState,
  action: CommentPaginationAction,
): CommentPaginationState {
  switch (action.type) {
    case 'request': {
      if (state.loading || state.error !== null) return state;
      if (state.pages.some((page) => page.page === action.page)) return state;
      const lastPage = state.pages[state.pages.length - 1];
      if (lastPage && !lastPage.hasMore) return state;
      return {
        ...state,
        loading: true,
        inFlightPage: action.page,
      };
    }
    case 'success': {
      if (state.inFlightPage !== action.page.page) return state;
      return {
        pages: [...state.pages, action.page],
        loading: false,
        error: null,
        inFlightPage: null,
      };
    }
    case 'failure': {
      if (state.inFlightPage !== action.page) return state;
      return {
        ...state,
        loading: false,
        error: action.error,
        inFlightPage: null,
      };
    }
    case 'retry': {
      if (state.error === null) return state;
      return { ...state, error: null };
    }
    case 'reset':
      return initialCommentPaginationState();
    default:
      return state;
  }
}

export function flattenComments(state: CommentPaginationState): SourceComment[] {
  return state.pages.flatMap((page) => page.items);
}

export function nextCommentPage(state: CommentPaginationState): number {
  const lastPage = state.pages[state.pages.length - 1];
  return lastPage ? lastPage.page + 1 : 1;
}

export function canLoadMoreComments(state: CommentPaginationState): boolean {
  if (state.loading || state.error !== null) return false;
  const lastPage = state.pages[state.pages.length - 1];
  return lastPage ? lastPage.hasMore : true;
}
